import { useEffect, useState } from 'react';
import { apiClient } from '../api/client';
import { DEFAULT_PANEL_IMAGES } from '../assets/panel-defaults';
import { SECTION_KEYS, type SectionKey } from '../components/attendee-mobile/constants';
import { useAuth } from '../contexts/AuthContext';

interface PanelImagesApiResponse {
  images: Record<string, { url: string; sizeKB: number } | null>;
}

export interface UsePanelImagesResult {
  panelImages: Record<SectionKey, string>;
  loading: boolean;
}

/**
 * Fetches panel images from the API once the auth token is available,
 * then merges admin-uploaded URLs with default fallback images per section.
 *
 * On API failure, gracefully degrades to all default images (no error toast).
 *
 * Requirements: 7.1, 7.4, 7.5, 8.4
 */
export function usePanelImages(): UsePanelImagesResult {
  const [panelImages, setPanelImages] = useState<Record<SectionKey, string>>(DEFAULT_PANEL_IMAGES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchPanelImages() {
      try {
        const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3000';
        const response = await fetch(`${apiBase}/panel-images`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json() as PanelImagesApiResponse;

        if (cancelled) return;

        const resolved = {} as Record<SectionKey, string>;
        for (const key of SECTION_KEYS) {
          const entry = data.images[key];
          if (entry?.url) {
            // Uploaded images are relative paths like /public/uploads/... — prepend API base
            resolved[key] = entry.url.startsWith('http') ? entry.url : `${apiBase}${entry.url}`;
          } else {
            resolved[key] = DEFAULT_PANEL_IMAGES[key];
          }
        }

        setPanelImages(resolved);
      } catch (err) {
        // Log the error so we can see why panel images aren't loading
        console.warn('[usePanelImages] Failed to fetch panel images:', err);
        // Graceful degradation: use all defaults on API failure
        if (!cancelled) {
          setPanelImages(DEFAULT_PANEL_IMAGES);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchPanelImages();

    return () => {
      cancelled = true;
    };
  }, []);

  return { panelImages, loading };
}
